import AuthService from './AuthService';
import SchoolService from '~/services/SchoolService';
import uuidv4 from '~/utils/uuidv4';
import Cookies from 'universal-cookie';
import UserConsentContext, {
    CONSENT_OPTIONS,
    CONSENT_IDENTIFIERS,
} from '~/services/UserConsentContext';

const cookies = new Cookies();

// The dimensions set up in Google Analytics
const EVENT_UNIQUE_ID = 'event_id';
const USER_ID_DIMENSION = 'user_id';
const SCHOOL_ID_DIMENSION = 'school_id';
const ATTRIBUTE1_DIMENSION = 'attribute_1';
const ATTRIBUTE2_DIMENSION = 'attribute_2';
const ATTRIBUTE3_DIMENSION = 'attribute_3';
const ATTRIBUTE4_DIMENSION = 'attribute_4';

// Custom dimensions configuration for gtag('config', ...)
const GA_DIMENSION_MAP = {
    dimension1: EVENT_UNIQUE_ID,
    dimension2: USER_ID_DIMENSION,
    dimension3: SCHOOL_ID_DIMENSION,
    dimension4: ATTRIBUTE1_DIMENSION,
    dimension5: ATTRIBUTE2_DIMENSION,
    dimension6: ATTRIBUTE3_DIMENSION,
    dimension7: ATTRIBUTE4_DIMENSION,
};

// Event Actions
const APPLY_CLICKED_ACTION = 'apply_click';
const JOBAD_VIEW_ACTION = 'jobad_view';
const COMPANY_VIEW_ACTION = 'company_view';
// const USER_LOGIN_ACTION = 'user_logged_in';
// const USER_VERIFIED_ACTION = 'user_verified';
const ARTICLE_VIEW_ACTION = 'article_view';
const VIDEO_VIEW_ACTION = 'video_view';
// const RESOURCE_VIEW_ACTION = 'resource_view';
const CHAT_CLICK_ACTION = 'chat_click';
const VFAIR_VISIT_ACTION = 'vfair_visit';
const EVENT_VIEW_ACTION = 'event_view';
const EVENT_CLICK_ACTION = 'event_click';
const VFAIRLIST_VIEW_ACTION = 'vfairlist_view';
const MASTERCLASSES_VIEW_ACTION = 'masterclasses_view';
const MYPROFILE_VIEW_ACTION = 'myprofile_view';
const MYPAGE_VIEW_ACTION = 'mypage_view';
const JOBADLIST_VIEW_ACTION = 'jobadlist_view';
const COMPANYLIST_VIEW_ACTION = 'companylist_view';
const ASSESSMENTLIST_VIEW_ACTION = 'assessmentlist_view';
const MYASSESSMENTS_VIEW_ACTION = 'myassessments_view';
const MYCALENDAR_VIEW_ACTION = 'mycalendar_view';
const RESOURCENODE_CLICK_ACTION = 'resourcenode_click';
const APPOINTMENTTYPE_CLICK_ACTION = 'appointmenttype_click';
const CV_UPLOAD_ACTION = 'cv_upload';
const CVTEMPLATE_CLICKS = 'cvtemplate_clicks';
const COUNTRYGUIDES_CLICKS = 'countryguides_clicks';
const ASSESSMENT_ACTION_CLICK = 'assessment_action_click';
const ASSESSMENT_AON_CONSENT = 'assessment_aon_consent';
const BOOTHLINKS_CLICK = 'boothlinks_click';

// Event categories
const JOBAD_CATEGORY = 'jobads';
const CONTENT_CATEGORY = 'company_content';
const VFAIR_CATEGORY = 'vfair_content';
const ASSESSMENT_CATEGORY = 'assessment';
const MISCELLANEOUS = 'miscellaneous';

// GA attributes
const EVENT_CATEGORY = 'event_category';
const EVENT_LABEL = 'event_label';
const PAGE_TITLE = 'page_title';
const PAGE_PATH = 'page_path';
const CUSTOM_MAP = 'custom_map';
const COOKIE_DOMAIN = 'cookie_domain';
const CLIENT_ID = 'ga_client_id';

const GA_DEFAULT_CONFIG = {
    [CUSTOM_MAP]: GA_DIMENSION_MAP,
    [COOKIE_DOMAIN]: 'auto',
};

// Ingestion API configurations
const INGESTION_API_URI = process.env.VUE_APP_ANALYTICS_INGESTION_API;
const INGESTION_METADATA_JOBAD_ID = 'job_id';
const INGESTION_METADATA_SCHOOL_ID = 'school_id';
const INGESTION_METADATA_COMPANY_ID = 'company_id';
const INGESTION_METADATA_VFAIR_ID = 'vfair_id';
const INGESTION_METADATA_PAVILION_ID = 'pavilion_id';
const INGESTION_METADATA_EVENT_ID = 'event_id';
const INGESTION_METADATA_ARTICLE_ID = 'article_id';
const INGESTION_METADATA_VIDEO_ID = 'video_id';
const INGESTION_METADATA_RESOURCENODE_ID = 'resource_id';
const INGESTION_METADATA_APPOINTMENTTYPE_ID = 'appointmenttype_id';
const INGESTION_METADATA_TYPE = 'type';
const INGESTION_METADATA_VARIANT = 'variant';
class AnalyticService {
    static clientId = '';

    // Event actions which require no data or additional metadata other than user and school platform
    static IMPRESSION_ACTIONS = {
        VFAIRLIST_VIEW_ACTION: VFAIRLIST_VIEW_ACTION,
        MASTERCLASSES_VIEW_ACTION: MASTERCLASSES_VIEW_ACTION,
        MYPROFILE_VIEW_ACTION: MYPROFILE_VIEW_ACTION,
        MYPAGE_VIEW_ACTION: MYPAGE_VIEW_ACTION,
        JOBADLIST_VIEW_ACTION: JOBADLIST_VIEW_ACTION,
        COMPANYLIST_VIEW_ACTION: COMPANYLIST_VIEW_ACTION,
        ASSESSMENTLIST_VIEW_ACTION: ASSESSMENTLIST_VIEW_ACTION,
        MYASSESSMENTS_VIEW_ACTION: MYASSESSMENTS_VIEW_ACTION,
        MYCALENDAR_VIEW_ACTION: MYCALENDAR_VIEW_ACTION,
        CV_UPLOAD_ACTION: CV_UPLOAD_ACTION,
        CVTEMPLATE_CLICKS: CVTEMPLATE_CLICKS,
        COUNTRYGUIDES_CLICKS: COUNTRYGUIDES_CLICKS,
        ASSESSMENT_AON_CONSENT: ASSESSMENT_AON_CONSENT,
    };
    //The gtag.js library includes a window property that, when set to true, disables gtag.js from sending data to Google Analytics.
    // To enable the library, set the property [disable] to false.
    static disableGoogleAnalytics(disable) {
        window[`ga-disable-${process.env.VUE_APP_GA_TRACKING_ID}`] = !!disable;
    }

    // Initialize initializes the AnalyticService dimension-mappings.
    static initialize() {
        if (!gtag) {
            return;
        }
        this.disableGoogleAnalytics(!UserConsentContext.getOption(CONSENT_OPTIONS.ANALYTICS));
        gtag('config', process.env.VUE_APP_GA_TRACKING_ID, GA_DEFAULT_CONFIG);
        this.getClientId(); // Initializes the anonymous client id
    }

    static getClientId() {
        // Get client id if it already exists
        if (this.clientId) {
            return this.clientId;
        }

        // Get client id from local storage
        let clientId = localStorage.getItem(CLIENT_ID);
        if (!clientId) {
            // Generate new clientId if it is not in localstorage
            this.clientId = uuidv4();
            localStorage.setItem(CLIENT_ID, this.clientId);
            clientId = this.clientId;
        }
        return clientId;
    }

    // logPageview sends a pageview-log to Google-Analytics with a given pagePath
    static logPageview(pagePath) {
        if (!UserConsentContext.getOption(CONSENT_OPTIONS.ANALYTICS)) {
            return;
        }

        // Validate input
        if (!gtag || !pagePath) {
            return;
        }

        gtag('config', process.env.VUE_APP_GA_TRACKING_ID, {
            [PAGE_TITLE]: document.title,
            [PAGE_PATH]: pagePath,
            ...GA_DEFAULT_CONFIG,
        });
    }

    // SendEvent sends an event by given name and with given data
    static async sendEvent(eventAction, category, data, metadata) {
        if (!UserConsentContext.getOption(CONSENT_OPTIONS.ANALYTICS)) {
            return;
        }

        let isAnonymous = false;

        // Include userId in event
        const userId = await AuthService.getUserId();
        if (userId !== null) {
            data[USER_ID_DIMENSION] = userId;
        } else {
            // If not logged in, send anonymous client-id instead
            const clientId = this.getClientId();
            data[USER_ID_DIMENSION] = `anonymous_${clientId}`;
            isAnonymous = true;
        }

        // Include schoolId in event
        const schoolId = SchoolService.getCurrentSchoolId();
        data[SCHOOL_ID_DIMENSION] = schoolId;

        // Send event
        // Check if gtag is initialized
        if (gtag) {
            gtag('event', eventAction, {
                [EVENT_UNIQUE_ID]: uuidv4(),
                [USER_ID_DIMENSION]: '',
                [ATTRIBUTE1_DIMENSION]: 'NULL',
                [ATTRIBUTE2_DIMENSION]: 'NULL',
                [ATTRIBUTE3_DIMENSION]: 'NULL',
                [ATTRIBUTE4_DIMENSION]: 'NULL',
                ...data,
                [EVENT_CATEGORY]: category,
                [EVENT_LABEL]: ''.concat(
                    SchoolService.getCurrentSchoolDomain(),
                    ' - ',
                    data[EVENT_LABEL] || ''
                ),
            });
        }

        // Build payload for ingestion API
        metadata[INGESTION_METADATA_SCHOOL_ID] = schoolId;
        const eventPayload = {
            user_id: data[USER_ID_DIMENSION],
            anonymous: isAnonymous,
            event_type: eventAction,
            timestamp: new Date().toISOString(),
            metadata: metadata,
        };

        fetch(INGESTION_API_URI, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(eventPayload),
        });
    }

    static applyClickEvent(jobAdId, companyId, jobTitle = null, vFairId = null, pavilionId = null) {
        // Validate jobAd-id
        if (!jobAdId) {
            return;
        }

        const data = {
            [ATTRIBUTE1_DIMENSION]: jobAdId,
        };

        let metadata = {
            [INGESTION_METADATA_JOBAD_ID]: jobAdId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        // Add companyId and jobtitle to the event label
        if (companyId || jobTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Job]${jobTitle || ''}`;
        }

        if (vFairId) {
            metadata[INGESTION_METADATA_VFAIR_ID] = vFairId;
        }

        if (pavilionId) {
            metadata[INGESTION_METADATA_PAVILION_ID] = pavilionId;
        }

        // Send event
        this.sendEvent(APPLY_CLICKED_ACTION, JOBAD_CATEGORY, data, metadata);
    }

    static jobadViewEvent(jobAdId, companyId, jobTitle = null, vFairId = null, pavilionId = null) {
        // Validate jobAd-id
        if (!jobAdId) {
            return;
        }

        const data = {
            [ATTRIBUTE1_DIMENSION]: jobAdId,
        };

        let metadata = {
            [INGESTION_METADATA_JOBAD_ID]: jobAdId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        if (vFairId) {
            metadata[INGESTION_METADATA_VFAIR_ID] = vFairId;
        }

        if (pavilionId) {
            metadata[INGESTION_METADATA_PAVILION_ID] = pavilionId;
        }

        // Add companyId and jobtitle to the event label
        if (companyId || jobTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Job]${jobTitle || ''}`;
        }

        // Send event
        this.sendEvent(JOBAD_VIEW_ACTION, JOBAD_CATEGORY, data, metadata);
    }

    static companyViewEvent(companyId, companyTitle = null, vFairId = null, pavilionId = null) {
        // Validate companyId
        if (!companyId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: companyId,
        };

        let metadata = {
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        if (vFairId) {
            metadata[INGESTION_METADATA_VFAIR_ID] = vFairId;
        }

        if (pavilionId) {
            metadata[INGESTION_METADATA_PAVILION_ID] = pavilionId;
        }

        // Add company title to the event label
        if (companyTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Company]${companyTitle}`;
        }

        // Send event
        this.sendEvent(COMPANY_VIEW_ACTION, JOBAD_CATEGORY, data, metadata);
    }

    static articleViewEvent(articleId, companyId, articleTitle = null) {
        // Validate articleId and companyId
        if (!articleId || !companyId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: articleId,
            [ATTRIBUTE2_DIMENSION]: companyId,
        };

        const metadata = {
            [INGESTION_METADATA_ARTICLE_ID]: articleId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        if (articleTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Article]${articleTitle}`;
        }

        // Send the event
        this.sendEvent(ARTICLE_VIEW_ACTION, CONTENT_CATEGORY, data, metadata);
    }

    static videoViewEvent(videoId, companyId, videoTitle = null) {
        // Validate videoId and companyId
        if (!videoId || !companyId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: videoId,
            [ATTRIBUTE2_DIMENSION]: companyId,
        };

        const metadata = {
            [INGESTION_METADATA_VIDEO_ID]: videoId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        if (videoTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Video]${videoTitle}`;
        }

        // Send the event
        this.sendEvent(VIDEO_VIEW_ACTION, CONTENT_CATEGORY, data, metadata);
    }

    static chatStartEvent(companyId, companyTitle = null) {
        // Validate companyId
        if (!companyId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: companyId,
        };

        const metadata = {
            [INGESTION_METADATA_COMPANY_ID]: companyId,
        };

        // Add company title to the event label
        if (companyTitle) {
            data[EVENT_LABEL] = `[CompanyId]${companyId} - [Company]${companyTitle}`;
        }

        // Send event
        this.sendEvent(CHAT_CLICK_ACTION, CONTENT_CATEGORY, data, metadata);
    }

    static vFairEvent(vFairId, vFairName = null, pavilionId = null) {
        if (!vFairId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: vFairId,
        };

        const metadata = {
            [INGESTION_METADATA_VFAIR_ID]: vFairId,
        };

        if (!!pavilionId) {
            metadata[INGESTION_METADATA_PAVILION_ID] = pavilionId;
        }

        data[EVENT_LABEL] = `[VFairId]${vFairId} - ${vFairName}`;

        // Send event
        this.sendEvent(VFAIR_VISIT_ACTION, VFAIR_CATEGORY, data, metadata);
    }

    static resourceNodeClickEvent(resourceId) {
        if (!resourceId) return;

        const data = { [ATTRIBUTE1_DIMENSION]: resourceId };
        const metadata = { [INGESTION_METADATA_RESOURCENODE_ID]: resourceId };

        data[EVENT_LABEL] = `[ResourceId]${resourceId}`;

        this.sendEvent(RESOURCENODE_CLICK_ACTION, MISCELLANEOUS, data, metadata);
    }

    static appointmentTypeClickEvent(appointmentTypeId) {
        if (!appointmentTypeId) return;

        const data = { [ATTRIBUTE1_DIMENSION]: appointmentTypeId };
        const metadata = { [INGESTION_METADATA_APPOINTMENTTYPE_ID]: appointmentTypeId };

        data[EVENT_LABEL] = `[AppointmentTypeId]${appointmentTypeId}`;

        this.sendEvent(APPOINTMENTTYPE_CLICK_ACTION, MISCELLANEOUS, data, metadata);
    }

    static boothLinksClickEvent(vFairId, pavilionId, companyId, linkType) {
        if (
            !vFairId ||
            !companyId ||
            ![
                'url',
                'email',
                'twitter',
                'facebook',
                'linked_in',
                'instagram',
                'you_tube',
                'vimeo',
                'we_chat',
            ].includes(linkType) // TODO: Remove this check when refactoring to typescript
        ) {
            return;
        }

        const data = { [ATTRIBUTE1_DIMENSION]: linkType };

        const metadata = {
            [INGESTION_METADATA_VFAIR_ID]: vFairId,
            [INGESTION_METADATA_PAVILION_ID]: pavilionId,
            [INGESTION_METADATA_COMPANY_ID]: companyId,
            link_type: linkType,
        };

        this.sendEvent(BOOTHLINKS_CLICK, MISCELLANEOUS, data, metadata);
    }

    static eventViewEvent(
        eventId,
        vFairId = null,
        organizationId = null,
        eventName = null,
        pavilionId = null
    ) {
        this.eventEvent(EVENT_VIEW_ACTION, eventId, vFairId, organizationId, eventName, pavilionId);
    }

    static eventClickEvent(
        eventId,
        vFairId = null,
        organizationId = null,
        eventName = null,
        pavilionId = null
    ) {
        this.eventEvent(
            EVENT_CLICK_ACTION,
            eventId,
            vFairId,
            organizationId,
            eventName,
            pavilionId
        );
    }

    static eventEvent(
        action,
        eventId,
        vFairId = null,
        organizationId = null,
        eventName = null,
        pavilionId = null
    ) {
        if (!eventId) {
            return;
        }
        // Initialize the event data to send
        const data = {
            [ATTRIBUTE1_DIMENSION]: eventId,
        };

        let metadata = {
            [INGESTION_METADATA_EVENT_ID]: eventId,
        };

        if (vFairId) {
            metadata[INGESTION_METADATA_VFAIR_ID] = vFairId;
        }

        if (organizationId) {
            metadata[INGESTION_METADATA_COMPANY_ID] = organizationId;
        }

        if (pavilionId) {
            metadata[INGESTION_METADATA_PAVILION_ID] = pavilionId;
        }

        data[EVENT_LABEL] = `[EventId]${eventId} - ${eventName}`;

        // Send event
        this.sendEvent(action, VFAIR_CATEGORY, data, metadata);
    }

    // Basic logging which only includes user and school platform data. May include custom metadata.
    static impressionEvent(eventName, metadata) {
        const impressionEventNames = Object.values(this.IMPRESSION_ACTIONS);
        if (!impressionEventNames.includes(eventName)) return;

        const data = {};
        const _metadata = metadata || {};

        this.sendEvent(eventName, MISCELLANEOUS, data, _metadata);
    }

    static assessmentActionClick(isPremium, hasPremium, hasStarted) {
        const data = {};

        let variant = 'START_TEST';
        if (isPremium && !hasPremium) {
            variant = 'PAYMENT';
        } else if (hasStarted) {
            variant = 'CONTINUE_TEST';
        }

        const metadata = { type: isPremium ? 'PREMIUM' : 'FREEMIUM', variant: variant };

        this.sendEvent(ASSESSMENT_ACTION_CLICK, ASSESSMENT_CATEGORY, data, metadata);
    }
}

export default AnalyticService;
